import React, { useState } from "react";
import { useCms } from "@/context/CmsContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Pencil, Check, RotateCcw, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * PART 8 — inline, role-gated editable copy.
 *  • Renders the current CMS value for `cmsKey` (or `fallback`).
 *  • Full-access roles (admin / leader) see a hover pencil that opens a popover
 *    editor; saves persist server-side and broadcast to all sessions.
 *  • Everyone else sees plain, read-only text.
 *
 * Usage (wrap the text node inside the existing heading/paragraph):
 *   <h2 className="..."><EditableText cmsKey="dash.group.health.title" fallback="Budget & Spend Health" /></h2>
 */
export function EditableText({ cmsKey, fallback = "", multiline = false, className = "", label = "Edit content" }) {
  const { t, setLocal, refresh } = useCms();
  const { isCmsAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const current = t(cmsKey, fallback);

  if (!isCmsAdmin) {
    return <span className={className} data-testid={`cms-text-${cmsKey}`}>{current}</span>;
  }

  const start = () => { setVal(t(cmsKey, fallback)); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      await api.saveCmsContent({ key: cmsKey, value: val, reason: "inline edit" });
      setLocal(cmsKey, val);
      toast.success("Content updated");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.resetCmsContent(cmsKey);
      setLocal(cmsKey, "");
      await refresh();
      toast.success("Reset to default");
      setOpen(false);
    } catch (e) {
      toast.error("Could not reset");
    } finally { setBusy(false); }
  };

  return (
    <span className={`group inline-flex items-center gap-1 ${className}`} data-testid={`cms-text-${cmsKey}`}>
      <span>{current}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" onClick={start} aria-label={label} title={label}
            data-testid={`cms-edit-${cmsKey}`}
            className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity duration-150 hover:text-[hsl(var(--primary))] focus-visible:opacity-100 focus-visible:outline-none group-hover:opacity-100">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80" data-testid={`cms-popover-${cmsKey}`}
          onClick={(e) => e.stopPropagation()}>
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
            {multiline ? (
              <Textarea value={val} onChange={(e) => setVal(e.target.value)} rows={4}
                className="text-sm" data-testid={`cms-input-${cmsKey}`} />
            ) : (
              <Input value={val} onChange={(e) => setVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                className="h-9 text-sm" data-testid={`cms-input-${cmsKey}`} />
            )}
            <div className="flex items-center justify-between gap-2 pt-0.5">
              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={reset} disabled={busy}
                data-testid={`cms-reset-${cmsKey}`}>
                <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset
              </Button>
              <div className="flex gap-1.5">
                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                <Button size="sm" className="h-8 px-3 text-xs" onClick={save} disabled={busy} data-testid={`cms-save-${cmsKey}`}>
                  {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />} Save
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </span>
  );
}
